import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OptimisationSummary, OptimisationType, ViewerId } from './types';

const KEY = ['ref-optimisation-list'];
const URL = '/api/ref-optimisation-list';

/** Throws with the server message (e.g. 403) so the page can show the refusal. */
async function parse(res: Response): Promise<OptimisationSummary[]> {
    if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? `Erreur ${res.status}`);
    }
    return (await res.json()) as OptimisationSummary[];
}

export function useOptimisations() {
    return useQuery({ queryKey: KEY, queryFn: async () => parse(await fetch(URL)) });
}

export function useOptimisationActions(viewer: ViewerId) {
    const qc = useQueryClient();
    const headers = { 'Content-Type': 'application/json', 'X-Viewer': viewer };
    const onSuccess = (data: OptimisationSummary[]) => qc.setQueryData(KEY, data);
    const create = useMutation({
        mutationFn: async (body: { type?: OptimisationType; copyOf?: string }) => parse(await fetch(URL, { method: 'POST', headers, body: JSON.stringify(body) })),
        onSuccess,
    });
    const remove = useMutation({
        mutationFn: async (eid: string) => parse(await fetch(`${URL}/${eid}`, { method: 'DELETE', headers })),
        onSuccess,
    });
    return { create, remove };
}

export function useResetOptimisations() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => parse(await fetch(`${URL}/reset`, { method: 'POST' })),
        onSuccess: (data) => qc.setQueryData(KEY, data),
    });
}
